import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams, useLocation, useNavigate } from 'react-router-dom';
import { useCampaignConfigLoader } from 'src/modules/campaignConfigBuilder/hooks/useCampaignConfigLoader';
import { useCampaignCreateOptionsLoader } from 'src/modules/campaignCreateOptions/hooks';
import { getCurrentCompanies, getCurrentCompanyId, setCurrentCompanyId } from 'src/modules/companies/slices';
import { isCompanyScopedPage, switchCompany } from 'src/helpers/helpers';
import { setAnalyticsCompany } from 'src/services/analytics';

export const useHandleSelectedCompany = () => {
  const dispatch = useDispatch();
  const selectedCompanyId = useSelector(getCurrentCompanyId);
  const companies = useSelector(getCurrentCompanies);
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const isInitialMount = useRef(true);
  const previousCompanyId = useRef<string | null>(null);
  const { loadCampaignConfigs } = useCampaignConfigLoader();
  const { loadCreateOptions } = useCampaignCreateOptionsLoader();

  useEffect(() => {
    const urlCompanyId = searchParams.get('companyId');
    const storedCompany = localStorage.getItem('bhiCurrentCompany');

    let companyToUse: string | null = null;
    
    if (urlCompanyId) {
      companyToUse = urlCompanyId;
    } else if (selectedCompanyId) {
      companyToUse = selectedCompanyId;
    } else if (storedCompany) {
      companyToUse = storedCompany;
    }

    if (companyToUse && companyToUse !== selectedCompanyId) {
      dispatch(setCurrentCompanyId(companyToUse));

      const newCompany = companies.find(c => c.id === companyToUse);
      if (newCompany) {
        setAnalyticsCompany(newCompany);
      }

      const hasCompanyChanged = !isInitialMount.current &&
                               previousCompanyId.current &&
                               previousCompanyId.current !== companyToUse;

      if (hasCompanyChanged) {
        // Client-side switch (no reload): the store still holds the previous
        // company's applicationId, so pass the new company's explicitly — it's
        // already in the loaded companies list — otherwise both fetches would
        // go out with the stale application and cache under the new company.
        const applicationId = newCompany?.applicationId;
        loadCampaignConfigs(true, companyToUse, applicationId);
        loadCreateOptions(true, companyToUse, applicationId);

        if (isCompanyScopedPage(location.pathname)) {
          const homeUrl = `/?companyId=${encodeURIComponent(companyToUse)}`;
          navigate(homeUrl, { replace: true });
        }
      }
    }
    
    if (companyToUse) {
      previousCompanyId.current = companyToUse;
    }

    if (isInitialMount.current && urlCompanyId) {
      localStorage.setItem('bhiCurrentCompany', urlCompanyId);
    }

    if (!urlCompanyId && companyToUse) {
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.set('companyId', companyToUse);
      setSearchParams(newSearchParams);
    }

    isInitialMount.current = false;
  }, [searchParams, selectedCompanyId, companies, dispatch, setSearchParams, location.pathname, navigate, loadCampaignConfigs, loadCreateOptions]);

  return {
    switchCompany,
  };
};
